// Servicio para manejar las gráficas personalizadas
class GraficasPersonalizadasService {
  constructor() {
    this.baseURL = process.env.REACT_APP_URL_API || 'http://localhost:4000/api';
    this.timeout = 10000; // 10 segundos
    this.modoDesarrollo = false; // Flag para modo desarrollo
  }

  // Método auxiliar para hacer peticiones HTTP
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    // Obtener token de autenticación
    const token = localStorage.getItem('accessToken');

    const config = {
      timeout: this.timeout,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers
      },
      ...options
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error en petición a ${endpoint}:`, error);
      throw error;
    }
  }

  // Obtener gráficas del usuario
  async getGraficasUsuario() {
    try {
      return await this.request('/dashboard/graficas-personalizadas');
    } catch (error) {
      console.error('Error getting graficas usuario:', error);

      // Si es error 404, activar modo desarrollo y devolver datos de prueba
      if (error.message.includes('404')) {
        console.warn('Backend no disponible, activando modo desarrollo');
        this.modoDesarrollo = true;
        return await this.getGraficasPrueba();
      }

      throw new Error('No se pudieron cargar las gráficas del usuario');
    }
  }

  // Crear nueva gráfica
  async crearGrafica(datosGrafica) {
    try {
      return await this.request('/dashboard/graficas-personalizadas', {
        method: 'POST',
        body: JSON.stringify(datosGrafica)
      });
    } catch (error) {
      console.error('Error creating grafica:', error);
      throw new Error('No se pudo crear la gráfica');
    }
  }

  // Actualizar gráfica existente
  async actualizarGrafica(graficaId, datosGrafica) {
    try {
      return await this.request(`/dashboard/graficas-personalizadas/${graficaId}`, {
        method: 'PUT',
        body: JSON.stringify(datosGrafica)
      });
    } catch (error) {
      console.error('Error updating grafica:', error);
      throw new Error('No se pudo actualizar la gráfica');
    }
  }

  // Eliminar gráfica
  async eliminarGrafica(graficaId) {
    try {
      return await this.request(`/dashboard/graficas-personalizadas/${graficaId}`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error('Error deleting grafica:', error);
      throw new Error('No se pudo eliminar la gráfica');
    }
  }

  // Toggle estado activo de gráfica
  async toggleActivaGrafica(graficaId, activa) {
    try {
      return await this.request(`/dashboard/graficas-personalizadas/${graficaId}`, {
        method: 'PUT',
        body: JSON.stringify({ activa })
      });
    } catch (error) {
      console.error('Error toggling grafica:', error);
      throw new Error('No se pudo cambiar el estado de la gráfica');
    }
  }

  // Reordenar gráficas
  async reordenarGraficas(graficasOrdenadas) {
    try {
      const graficas = graficasOrdenadas.map((grafica, index) => ({
        _id: grafica._id,
        orden: index
      }));

      return await this.request('/dashboard/graficas-personalizadas/reordenar', {
        method: 'PUT',
        body: JSON.stringify({ graficas })
      });
    } catch (error) {
      console.error('Error reordering graficas:', error);
      throw new Error('No se pudo reordenar las gráficas');
    }
  }

  // Obtener datos de una gráfica específica
  async getDatosGrafica(graficaId, tipoGrafica) {
    try {
      // Para medidores no se necesitan query params según la documentación
      const endpoint = `/dashboard/graficas-personalizadas/${graficaId}/datos`;
      return await this.request(endpoint);
    } catch (error) {
      console.error('Error getting datos grafica:', error);

      // Si es error 404, devolver datos de prueba
      if (error.message.includes('404')) {
        console.warn('Backend no disponible, devolviendo datos de prueba para gráfica:', graficaId);
        return await this.getDatosGraficaPrueba(graficaId, tipoGrafica);
      }

      throw new Error('No se pudieron cargar los datos de la gráfica');
    }
  }

  // Obtener sensores disponibles
  async getSensoresDisponibles() {
    try {
      return await this.request('/dashboard/sensores-disponibles');
    } catch (error) {
      console.error('Error getting sensores disponibles:', error);

      // Si es error 404, activar modo desarrollo y devolver datos de prueba
      if (error.message.includes('404')) {
        console.warn('Backend no disponible, activando modo desarrollo');
        this.modoDesarrollo = true;
        return await this.getSensoresPrueba();
      }

      throw new Error('No se pudieron cargar los sensores disponibles');
    }
  }

  // ============= DATOS DE PRUEBA PARA DESARROLLO =============

  // Generar datos de prueba para gráficas
  async getGraficasPrueba() {
    // Simular delay de red
    await new Promise(resolve => setTimeout(resolve, 800));

    const graficasPrueba = [
      {
        _id: 'prueba-1',
        titulo: 'Temperatura Ambiente',
        descripcion: 'Monitoreo de temperatura en tiempo real',
        tipoGrafica: 'medidor',
        tipoAgregacion: 'promedio_diario',
        sensor: {
          _id: 'sensor-temp-1',
          alias: 'Temp-Sala-01',
          nombre: 'Sensor de Temperatura DHT22',
          tipo: { nombre: 'Temperatura' },
          rangoMin: 0,
          rangoMax: 50,
          unidades: '°C',
          estatusDispositivo: true
        },
        configuracion: {
          mostrarLimites: true,
          colorBarra: '#ff6b35',
          mostrarPorcentaje: false,
          formatoValor: 'decimal'
        },
        activa: true,
        orden: 0,
        fechaCreacion: new Date().toISOString(),
        fechaActualizacion: new Date().toISOString()
      },
      {
        _id: 'prueba-2',
        titulo: 'Humedad Relativa',
        descripcion: 'Control de humedad del ambiente',
        tipoGrafica: 'medidor',
        tipoAgregacion: 'promedio_diario',
        sensor: {
          _id: 'sensor-hum-1',
          alias: 'Hum-Sala-01',
          nombre: 'Sensor de Humedad DHT22',
          tipo: { nombre: 'Humedad' },
          rangoMin: 0,
          rangoMax: 100,
          unidades: '%',
          estatusDispositivo: true
        },
        configuracion: {
          mostrarLimites: true,
          colorBarra: '#4ecdc4',
          mostrarPorcentaje: true,
          formatoValor: 'entero'
        },
        activa: true,
        orden: 1,
        fechaCreacion: new Date().toISOString(),
        fechaActualizacion: new Date().toISOString()
      },
      {
        _id: 'prueba-3',
        titulo: 'Presión Atmosférica',
        descripción: 'Medición de presión barométrica',
        tipoGrafica: 'medidor',
        tipoAgregacion: 'promedio_diario',
        sensor: {
          _id: 'sensor-pres-1',
          alias: 'Pres-Ext-01',
          nombre: 'Sensor de Presión BMP280',
          tipo: { nombre: 'Presión' },
          rangoMin: 950,
          rangoMax: 1050,
          unidades: 'hPa',
          estatusDispositivo: true
        },
        configuracion: {
          mostrarLimites: false,
          colorBarra: '#a8e6cf',
          mostrarPorcentaje: false,
          formatoValor: 'decimal'
        },
        activa: true,
        orden: 2,
        fechaCreacion: new Date().toISOString(),
        fechaActualizacion: new Date().toISOString()
      }
    ];

    return {
      success: true,
      data: graficasPrueba,
      message: 'Gráficas de prueba cargadas'
    };
  }

  // Generar datos de prueba para un medidor específico
  async getDatosPruebaMedidor(graficaId) {
    // Simular delay de red
    await new Promise(resolve => setTimeout(resolve, 500));

    // Generar datos según el ID de la gráfica
    let datosBase;

    switch (graficaId) {
      case 'prueba-1': // Temperatura
        datosBase = {
          valorActual: 22.5 + (Math.random() - 0.5) * 4, // 20.5 - 24.5°C
          valorMinimo: 0,
          valorMaximo: 50,
          unidades: '°C',
          estadisticas: {
            promedioMes: 22.8,
            minimoMes: 18.2,
            maximoMes: 26.1
          }
        };
        break;

      case 'prueba-2': // Humedad
        datosBase = {
          valorActual: 65 + (Math.random() - 0.5) * 20, // 55 - 75%
          valorMinimo: 0,
          valorMaximo: 100,
          unidades: '%',
          estadisticas: {
            promedioMes: 67.3,
            minimoMes: 45.8,
            maximoMes: 82.1
          }
        };
        break;

      case 'prueba-3': // Presión
        datosBase = {
          valorActual: 1013.25 + (Math.random() - 0.5) * 20, // 1003 - 1023 hPa
          valorMinimo: 950,
          valorMaximo: 1050,
          unidades: 'hPa',
          estadisticas: {
            promedioMes: 1012.8,
            minimoMes: 1008.2,
            maximoMes: 1018.7
          }
        };
        break;

      default:
        datosBase = {
          valorActual: 50 + (Math.random() - 0.5) * 40,
          valorMinimo: 0,
          valorMaximo: 100,
          unidades: 'units',
          estadisticas: {
            promedioMes: 52.3,
            minimoMes: 25.1,
            maximoMes: 78.9
          }
        };
    }

    // Calcular porcentaje según la documentación
    const rango = datosBase.valorMaximo - datosBase.valorMinimo;
    const porcentaje = ((datosBase.valorActual - datosBase.valorMinimo) / rango) * 100;

    // Determinar estado según la documentación
    let estado = 'normal';
    if (porcentaje <= 20) {
      estado = 'advertencia';
    } else if (porcentaje >= 80) {
      estado = 'peligro';
    }

    return {
      success: true,
      tipoGrafica: 'medidor',
      data: {
        valorActual: Math.round(datosBase.valorActual * 100) / 100,
        valorMinimo: datosBase.valorMinimo,
        valorMaximo: datosBase.valorMaximo,
        unidades: datosBase.unidades,
        porcentaje: Math.round(porcentaje * 100) / 100,
        estado: estado,
        estadisticas: datosBase.estadisticas,
        ultimaActualizacion: new Date().toISOString()
      }
    };
  }

  // Obtener datos de prueba para una gráfica específica
  async getDatosGraficaPrueba(graficaId, tipoGrafica = 'medidor') {
    // Simular delay de red
    await new Promise(resolve => setTimeout(resolve, 500));

    if (tipoGrafica === 'medidor') {
      return await this.getDatosPruebaMedidor(graficaId);
    }

    // Para gráficas de líneas (implementación futura)
    const now = new Date();
    const datos = [];

    // Generar 30 días de datos
    for (let i = 29; i >= 0; i--) {
      const fecha = new Date(now);
      fecha.setDate(fecha.getDate() - i);

      let valor;
      switch (graficaId) {
        case 'prueba-1': // Temperatura
          valor = 22 + Math.sin(i * 0.2) * 3 + (Math.random() - 0.5) * 2;
          break;
        case 'prueba-2': // Humedad
          valor = 65 + Math.cos(i * 0.15) * 10 + (Math.random() - 0.5) * 5;
          break;
        case 'prueba-3': // Presión
          valor = 1013 + Math.sin(i * 0.1) * 8 + (Math.random() - 0.5) * 3;
          break;
        default:
          valor = 50 + Math.sin(i * 0.3) * 20 + (Math.random() - 0.5) * 10;
      }

      datos.push({
        date: fecha.toISOString(),
        value: Math.round(valor * 100) / 100
      });
    }

    return {
      success: true,
      tipoGrafica: 'lineas',
      data: {
        labels: datos.map(d => d.date.split('T')[0]),
        datasets: [{
          label: 'Valores históricos',
          data: datos.map(d => d.value),
          borderColor: '#1890ff',
          backgroundColor: 'rgba(24, 144, 255, 0.1)',
          fill: false,
          tension: 0.1,
          pointRadius: 3
        }],
        estadisticas: {
          promedio: datos.reduce((sum, d) => sum + d.value, 0) / datos.length,
          minimo: Math.min(...datos.map(d => d.value)),
          maximo: Math.max(...datos.map(d => d.value)),
          tendencia: 'estable'
        },
        rangoSensor: {
          min: 0,
          max: 100,
          unidades: 'units'
        }
      }
    };
  }

  // Obtener sensores de prueba
  async getSensoresPrueba() {
    await new Promise(resolve => setTimeout(resolve, 300));

    const sensoresPrueba = [
      {
        _id: 'sensor-temp-1',
        alias: 'Temp-Sala-01',
        nombre: 'Sensor de Temperatura DHT22',
        tipo: { nombre: 'Temperatura' },
        rangoMin: 0,
        rangoMax: 50,
        unidades: '°C',
        estatusDispositivo: true
      },
      {
        _id: 'sensor-hum-1',
        alias: 'Hum-Sala-01',
        nombre: 'Sensor de Humedad DHT22',
        tipo: { nombre: 'Humedad' },
        rangoMin: 0,
        rangoMax: 100,
        unidades: '%',
        estatusDispositivo: true
      },
      {
        _id: 'sensor-pres-1',
        alias: 'Pres-Ext-01',
        nombre: 'Sensor de Presión BMP280',
        tipo: { nombre: 'Presión' },
        rangoMin: 950,
        rangoMax: 1050,
        unidades: 'hPa',
        estatusDispositivo: true
      },
      {
        _id: 'sensor-luz-1',
        alias: 'Luz-Jardín-01',
        nombre: 'Sensor de Luminosidad LDR',
        tipo: { nombre: 'Luminosidad' },
        rangoMin: 0,
        rangoMax: 1024,
        unidades: 'lux',
        estatusDispositivo: false
      },
      {
        _id: 'sensor-ph-1',
        alias: 'pH-Agua-01',
        nombre: 'Sensor de pH',
        tipo: { nombre: 'pH' },
        rangoMin: 0,
        rangoMax: 14,
        unidades: 'pH',
        estatusDispositivo: true
      }
    ];

    return {
      success: true,
      data: sensoresPrueba,
      message: 'Sensores de prueba cargados'
    };
  }

  // Simular operaciones CRUD para modo desarrollo
  async operacionPrueba(operacion, datos = null) {
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    return {
      success: true,
      data: datos,
      message: `Operación ${operacion} simulada exitosamente`
    };
  }
}

// Exportar instancia única del servicio
const graficasPersonalizadasService = new GraficasPersonalizadasService();
export default graficasPersonalizadasService;